import moment from 'moment';
import { filterFactory } from './filter.factory';

/**
 * Converts a filter value to a moment object.
 *
 * @param {Date | string} filter - The filter value to be converted.
 * @returns {moment.Moment} - The moment object representing the filter value.
 */
const getFilterMoment = (filter: Date | string): moment.Moment => {
  // Regular expression to match dates in the format DD.MM.YYYY
  const dottedDateRegex =
    /^(([1-2][0-9])|(0[1-9])|(3[0-1]))\.((1[0-2])|(0[1-9]))\.[0-9]{4}$/;

  // If the filter is a Date object, convert it to a moment object
  if (filter instanceof Date) {
    return moment(filter);
  }

  // If the filter is a string, check if it matches the dotted date format
  if (typeof filter === 'string') {
    if (dottedDateRegex.test(filter)) {
      return moment(filter, 'DD.MM.YYYY');
    }

    // Convert the string to a moment object
    return moment(filter);
  }

  // Convert the filter to a moment object
  return moment(filter);
};

/**
 * Filters dates based on a filter value.
 *
 * @param {Date | string | undefined | null} value - The date value to be filtered.
 * @param {Date | string | Date[]} filterValue - The filter value to filter by.
 * @returns {boolean} - True if the date matches the filter, false otherwise.
 */
export const dateFilter = filterFactory<
  Date | string | undefined | null,
  Date | string | Date[]
>((value, filterValue) => {
  // Return false if the value is undefined, null, or an empty string
  if (value === undefined || value === null || value === '') {
    return false;
  }

  // Convert the value to a moment object
  const date = getFilterMoment(value);
  let filter = filterValue;

  // If the filter is an array, handle date range filtering
  if (Array.isArray(filter)) {
    if (filter.length === 0) {
      return false;
    }

    if (filter[0] && filter[1]) {
      const start = moment(filter[0]);
      const end = moment(filter[1]);

      // Check if the date is within the range
      if (start.isSameOrBefore(date) && end.isSameOrAfter(date)) {
        return true;
      }

      return false;
    }

    if (!filter[0]) {
      // If it is a multi-date picker but only one date is selected
      return false;
    }

    [filter] = filter;
  }

  // Convert the filter to a moment object
  const newFilter = getFilterMoment(filter);

  // Check if the date matches the filter
  return date.format('DD.MM.YYYY').includes(newFilter.format('DD.MM.YYYY'));
});
